import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { FaCamera, FaTrash, FaSpinner, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { uploadFile, validateFile, generateUniqueFilename, UploadProgress } from '../firebase/storage';

const Container = styled.div`
  border: 2px dashed ${theme.colors.mediumGray};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xl};
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.lightGray};
  }
  
  &.has-images {
    border-style: solid;
    border-color: ${theme.colors.primary};
    background: ${theme.colors.white};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadIcon = styled.div`
  font-size: 2rem;
  color: ${theme.colors.mediumGray};
  margin-bottom: ${theme.spacing.md};
`;

const UploadText = styled.div`
  color: ${theme.colors.mediumGray};
  font-weight: 500;
  margin-bottom: ${theme.spacing.sm};
`;

const UploadSubtext = styled.div`
  color: ${theme.colors.lightGray};
  font-size: 0.9rem;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: ${theme.spacing.md};
  width: 100%;
  margin-top: ${theme.spacing.md};
`;

const ImageItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  background: ${theme.colors.lightGray};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${ImageItem}:hover & {
    opacity: 1;
  }
`;

const DeleteButton = styled.button`
  background: ${theme.colors.error};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #dc2626;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${theme.colors.lightGray};
  border-radius: 2px;
  overflow: hidden;
  margin-top: ${theme.spacing.sm};
`;

const ProgressFill = styled.div<{ progress: number }>`
  width: ${props => props.progress}%;
  height: 100%;
  background: ${theme.colors.primary};
  transition: width 0.3s ease;
`;

const StatusMessage = styled.div<{ type: 'success' | 'error' | 'info' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.9rem;
  font-weight: 500;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'error':
        return `
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      case 'info':
        return `
          background: #d1ecf1;
          color: #0c5460;
          border: 1px solid #bee5eb;
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

interface ImageUploadProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  maxImages?: number;
  userId?: string;
  carId?: string;
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  images,
  onImagesChange,
  maxImages = 5,
  userId = 'anonymous',
  carId = 'default',
  disabled = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file
    const validation = validateFile(file, 5, ['image/jpeg', 'image/png', 'image/webp']);
    if (!validation.isValid) {
      setStatusMessage({ type: 'error', text: validation.error! });
      return;
    }

    // Check if we can add more images
    if (images.length >= maxImages) {
      setStatusMessage({ type: 'error', text: `Maximum ${maxImages} images allowed` });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setStatusMessage({ type: 'info', text: 'Uploading image...' });

    try {
      // Generate unique filename
      const filename = generateUniqueFilename(file.name);
      const path = `cars/${userId}/${carId}/${filename}`;

      // Upload file
      const result = await uploadFile(file, path, (progress: UploadProgress) => {
        setUploadProgress(progress.progress);
      });

      // Add to images array
      const newImages = [...images, result.url];
      onImagesChange(newImages);

      setStatusMessage({ type: 'success', text: 'Image uploaded successfully!' });
      
      // Clear status message after 3 seconds
      setTimeout(() => setStatusMessage(null), 3000);
    } catch (error) {
      console.error('Upload error:', error);
      setStatusMessage({ type: 'error', text: 'Failed to upload image. Please try again.' });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const hasImages = images.length > 0;

  return (
    <Container 
      className={hasImages ? 'has-images' : ''}
      onClick={handleClick}
    >
      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        disabled={disabled || isUploading}
      />
      
      {!hasImages && !isUploading && (
        <>
          <UploadIcon>
            <FaCamera />
          </UploadIcon>
          <UploadText>Tap to add photos</UploadText>
          <UploadSubtext>JPG, PNG, WebP up to 5MB</UploadSubtext>
        </>
      )}

      {isUploading && (
        <>
          <UploadIcon>
            <LoadingSpinner>
              <FaSpinner />
            </LoadingSpinner>
          </UploadIcon>
          <UploadText>Uploading...</UploadText>
          <ProgressBar>
            <ProgressFill progress={uploadProgress} />
          </ProgressBar>
        </>
      )}

      {hasImages && !isUploading && (
        <>
          <UploadText>Add more photos ({images.length}/{maxImages})</UploadText>
          <ImageGrid>
            {images.map((imageUrl, index) => (
              <ImageItem key={index}>
                <Image src={imageUrl} alt={`Car photo ${index + 1}`} />
                <ImageOverlay>
                  <DeleteButton onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(index);
                  }}>
                    <FaTrash />
                  </DeleteButton>
                </ImageOverlay>
              </ImageItem>
            ))}
          </ImageGrid>
        </>
      )}

      {statusMessage && (
        <StatusMessage type={statusMessage.type}>
          {statusMessage.type === 'success' && <FaCheck />}
          {statusMessage.type === 'error' && <FaExclamationTriangle />}
          {statusMessage.type === 'info' && <FaSpinner className="animate-spin" />}
          {statusMessage.text}
        </StatusMessage>
      )}
    </Container>
  );
};

export default ImageUpload;
